<template>
  <div id="clientCases-list" class="container-fluid item-list pb-16 cases-v2-wide">
    <v-list-header
      :title="$t('clientTour.cases')"
      icon="compass"
      @open-filters-offcanvas="openOffCanvas('the-case-filters-offcanvas')"
      @emit-close-off-canvas="closeOffCanvas"
      @reload-cases="loadClientCases"
    >
      <template #input-search>
        <v-input-text-search
          id="clientCases-search"
          v-model="clientName"
          :placeholder="$t('general.shared.search')"
          autocomplete="off"
          class="w-100 me-24"
          input-class="h-auto py-4"
          input-group-text-class="py-4 px-16"
          type="text"
          @update:modelValue="
            clientName = $event;
            search();
          "
        />
      </template>
    </v-list-header>

    <div class="items-content">
      <div class="filters-container mb-12">
        <v-filters v-if="!!filterOptions" v-model="filterOptions" @remove-filter="removeFilter" />
      </div>

      <!-- Compact Filter Section -->
      <div class="row g-3 mb-12 align-items-end filter-section-compact">
        <div class="col-auto align-self-center">
          <v-checkbox
            id="case-filter-only-on-way-select"
            v-model="onlyOnWay"
            :label="t('general.shared.seenOnlyCasesOnWay')"
            :left="true"
            :right="false"
            class="form-check-inline mb-0"
            for-key="caseFilterOnlyOnWaySelect"
            @update:modelValue="doSearch"
          />
        </div>
        <div class="col">
          <v-form
            id="case-filter-reservation-status-select"
            :label="$t('general.shared.reservations')"
            form-type="outline"
            class="mb-0"
          >
            <v-select
              v-model="reservationStatus"
              :clearable="false"
              :options="reservationStatusOptions"
              :reduce="(returnValue: { key: any; }) => returnValue.key"
              @update:modelValue="doSearch"
              class="v-select-compact"
            >
              <template v-slot:no-options>
                <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
              </template>
            </v-select>
          </v-form>
        </div>

        <div class="col">
          <v-form
            id="case-filter-payments-status-select"
            :label="$t('general.shared.payments')"
            form-type="outline"
            class="mb-0"
          >
            <v-select
              v-model="paymentsStatus"
              :clearable="false"
              :options="paymentsStatusOptions"
              :reduce="(returnValue: { key: any; }) => returnValue.key"
              @update:modelValue="doSearch"
              class="v-select-compact"
            >
              <template v-slot:no-options>
                <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
              </template>
            </v-select>
          </v-form>
        </div>

        <div class="col">
          <v-form
            id="case-filter-invoices-status-select"
            :label="$t('general.shared.invoices')"
            form-type="outline"
            class="mb-0"
          >
            <v-select
              v-model="invoicesStatus"
              :clearable="false"
              :options="invoicesStatusOptions"
              :reduce="(returnValue: { key: any; }) => returnValue.key"
              @update:modelValue="doSearch"
              class="v-select-compact"
            >
              <template v-slot:no-options>
                <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
              </template>
            </v-select>
          </v-form>
        </div>

        <div class="col">
          <v-form-input
            id="case-filter-case-starting-select"
            v-model="caseStartingInDays"
            label="Starting in Days"
            autocomplete="off"
            type="number"
            form-type="outline"
            class="form-control-compact mb-0"
            @blur="doSearch"
          />
        </div>

        <div class="col">
          <v-form-input
            id="case-filter-hotel-paid-select"
            v-model="hotelPaidUntil"
            label="Hotel paid Days"
            autocomplete="off"
            type="number"
            form-type="outline"
            class="form-control-compact mb-0"
            @blur="doSearch"
          />
        </div>
      </div>

      <div class="cases-list-table-shell mt-12">
        <div class="table-responsive d-none d-lg-flex table-for-cases">
        <v-table>
          <v-table-header>
            <tr>
              <th class="fit" />
              <th>
                <v-button :class="{ 'sort-active': order.key === 'name' }" class="btn-xs btn-icon btn btn-order"
                  @click="toggleOrder('name')">
                  {{ $t('general.shared.name') }}
                  <v-icon :class="{ 'sort-icon-transition': order.key === 'name' && order.order === 'DESC' }"
                    icon="chevron-down" />
                </v-button>
              </th>
              <th>{{ $t('general.shared.type') }}</th>
              <th>{{ $t('general.shared.originAndDestination') }}</th>
              <th>{{ $t('general.shared.pax') }}</th>

              <th>
                <v-button :class="{ 'sort-active': order.key === 'init_date' }" class="btn-xs btn-icon btn btn-order"
                  @click="toggleOrder('init_date')">
                  {{ $t('general.shared.initDate') }}
                  <v-icon :class="{ 'sort-icon-transition': order.key === 'init_date' && order.order === 'DESC' }"
                    icon="chevron-down" />
                </v-button>
              </th>

              <th>Status</th>
              <th class="fit" />
            </tr>
          </v-table-header>

          <v-table-body class="fw-medium">
            <tr v-if="!loaded">
              <td class="position-relative border-0 py-96" colspan="8">
                <v-loader />
              </td>
            </tr>

            <template v-else-if="!!clientCases && clientCases.length > 0">
              <template v-for="(clientCase, idx) in clientCases" :key="clientCase.id">
                <tr>
                  <td>
                    <v-button variant="icon" class="p-0" @click="toggleAccordion(idx)">
                      <v-icon :icon="openIndex === idx ? 'chevron-down' : 'chevron-right'" size="xs" />
                    </v-button>
                  </td>
                  <td class="d-flex align-items-center">
                    <div class="image-container rounded-sm bg-primary me-12"
                      :class="{
                        'bg-collaborator': clientCase.assignedTo?.agency?.isCollaborator,
                        'draft': clientCase.is_draft,
                        'web-booking': clientCase.is_web_booking
                      }">
                      <template v-if="!!clientCase.client && !!clientCase.client.name">
                        {{ clientCase.client.name.charAt(0) }}
                      </template>
                      <template v-else>{{ clientCase.id }}</template>
                    </div>
                    <router-link v-if="!clientCase.deletedAt" :aria-label="clientCase.name" :to="{
                      name: 'case-v2.setup.show',
                      params: { caseId: clientCase.id }
                    }" class="py-4 fw-bold">
                      {{ clientCase.name }}
                    </router-link>
                    <a v-else :aria-label="clientCase.name" class="py-4 fw-bold" href="#"
                      @click.prevent="setCurrentclientCase(clientCase)">
                      {{ clientCase.name }}
                    </a>
                  </td>

                  <td class="text-center">
                    <div class="case-type-badge-v1 bg-primary text-white">
                      {{ findConceptByKey("tour.travel_type", clientCase?.travel_type)?.name?.charAt(0) }}
                    </div>
                  </td>

                  <td>
                    <template v-if="!!clientCase?.origin || !!clientCase?.destination">
                      {{ !!clientCase?.origin ? clientCase?.origin.name : '-' }}
                      <v-icon icon="arrow-right" size="xs" class="px-8" />
                      {{ !!clientCase.destination ? clientCase.destination.name : '-' }}
                    </template>
                    <template v-else>-</template>
                  </td>

                  <td>{{ clientCase.pax_count }}</td>

                  <td class="fw-bold">{{ $str.formatDateTime(clientCase.start_date, settings.formatDate, false) }}</td>

                  <td>
                    <div class="d-flex flex-wrap gap-4 align-items-center">
                      <template v-if="clientCase.is_draft"> - </template>
                      <template v-else>
                        <div class="tooltip-wrapper">
                          <div class="category-badge category-Q">Q</div>
                          <div class="tooltip-text">Quoted</div>
                        </div>
                        <div class="tooltip-wrapper">
                          <div class="category-badge category-I">I</div>
                          <div class="tooltip-text">Invoiced</div>
                        </div>
                        <div class="tooltip-wrapper">
                          <div class="category-badge category-T">T</div>
                          <div class="tooltip-text">Accountability</div>
                        </div>
                        <div class="tooltip-wrapper">
                          <div class="category-badge category-A">A</div>
                          <div class="tooltip-text">Travel Document Sent</div>
                        </div>
                        <div class="tooltip-wrapper">
                          <div class="category-badge category-L">L</div>
                          <div class="tooltip-text">Luggage Label Sent</div>
                        </div>
                      </template>
                    </div>
                  </td>

                  <td class="fit text-end">
                    <div class="d-flex align-items-center gap-4">
                      <v-button
                        size="xs"
                        variant="icon"
                        class="btn-view bg-primary text-white"
                        title="Duplicate Case"
                        @click="duplicateCase = true; caseId = Number(clientCase.id);"
                      >
                        <span class="mi mi-copy text-sm" />
                      </v-button>

                      <v-button
                        v-if="!clientCase.is_draft"
                        size="xs"
                        variant="icon"
                        class="btn-view bg-primary text-white"
                        title="Connect Luggage"
                        @click="caseConnectLuggage = true; caseId = Number(clientCase.id);"
                      >
                        <v-icon icon="luggage-cart" size="xs" />
                      </v-button>

                      <v-button
                        size="xs"
                        variant="icon"
                        class="btn-view bg-primary text-white"
                        :class="{ 'cutom-notes': !!clientCase?.invoice_notes }"
                        title="Custom Notes"
                        @click="customNotes = true; caseId = Number(clientCase.id); invoiceNotes = clientCase?.invoice_notes ?? '';"
                      >
                        <v-icon icon="comment" size="xs" />
                      </v-button>

                      <v-button
                        v-if="!clientCase.is_draft"
                        size="xs"
                        variant="icon"
                        class="btn-view bg-secondary text-white"
                        title="Case History"
                        @click="openCaseHistoryModal(clientCase)"
                      >
                        <v-icon icon="history" size="xs" />
                      </v-button>

                      <v-button
                        size="xs"
                        variant="icon"
                        class="btn-view bg-primary text-white"
                        title="Preview Details"
                        @click="openCaseDetailsModal(clientCase); generalShow = true;"
                      >
                        <v-icon icon="eye" size="xs" />
                      </v-button>

                      <router-link
                        v-if="!clientCase.deletedAt"
                        :aria-label="$t('tourClient.tour')"
                        :to="{ name: 'case-v2.setup.show', params: { caseId: clientCase.id } }"
                        class="btn btn-icon btn-view rounded-pill bg-secondary"
                        title="Go to Setup"
                      >
                        <v-icon icon="arrow-right" size="xs" />
                      </router-link>

                      <v-button
                        v-else
                        class="btn-view rounded-pill bg-secondary"
                        size="xs"
                        variant="icon"
                        title="Archived"
                        @click="setCurrentclientCase(clientCase)"
                      >
                        <v-icon icon="arrow-right" size="xs" />
                      </v-button>
                    </div>
                  </td>
                </tr>
                <tr v-if="openIndex === idx">
                  <td colspan="8" class="p-0">
                    <div class="bg-light p-16">
                      <div class="table-responsive">
                        <table class="table table-bordered table-sm mb-0">
                          <thead class="table-light">
                            <tr>
                              <th>Category</th>
                              <th>Payment</th>
                              <th>Invoice</th>
                              <th>Booking</th>
                            </tr>
                          </thead>
                          <tbody>
                            <template v-if="clientCase.extras && Object.keys(clientCase.extras).length > 0">
                              <tr v-for="(values, category) in clientCase.extras" :key="category">
                                <td class="text-capitalize">{{ category }}</td>
                                <td>{{ values.payment }}</td>
                                <td>{{ values.invoice }}</td>
                                <td>{{ values.booking }}</td>
                              </tr>
                            </template>
                            <tr v-else>
                              <td colspan="4" class="text-center">No extra details available</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </template>

            <template v-else>
              <tr>
                <td class="rounded-xs py-16 text-center" colspan="8">
                  No Cases available
                </td>
              </tr>
            </template>
          </v-table-body>
        </v-table>
        </div>
      </div>

      <div class="item-cards d-block d-lg-none">
        <div v-if="!loaded" class="position-relative py-96">
          <v-loader />
        </div>

        <template v-else-if="!!clientCases && clientCases.length > 0">
          <div
            v-for="(clientCase, idx) in clientCases"
            :key="clientCase.id"
            class="item-card border rounded-xs p-8 mb-12 text-sm"
          >
            <div class="d-flex">
              <div class="image-container rounded-sm bg-primary me-12" :class="{ draft: clientCase.is_draft, 'web-booking': clientCase.is_web_booking }">
                <template v-if="!!clientCase.client && !!clientCase.client.name">
                  {{ clientCase.client.name.charAt(0) }}
                </template>

                <template v-else>{{ clientCase.id }}</template>
              </div>

              <div class="w-100">
                <router-link
                  :aria-label="clientCase.name"
                  :to="{ name: 'case-v2.setup.show', params: { caseId: clientCase.id } }"
                  class="fw-bold"
                >
                  {{ clientCase.name }}
                </router-link>

                <div class="text-xs fw-bold mt-4">
                  {{ $str.formatDateTime(clientCase.start_date, settings.formatDate) }}
                  -
                  {{
                    !!clientCase.end_date ? $str.formatDateTime(clientCase.end_date, settings.formatDate) : "-"
                  }}
                </div>

                <div class="text-xs fw-light mt-4">
                  <template v-if="!!clientCase.origin || !!clientCase?.destination">
                    {{ !!clientCase.origin ? clientCase.origin.name : "-" }}

                    <v-icon icon="arrow-right" size="xs" space="px-8" />

                    {{ !!clientCase.destination ? clientCase.destination.name : "-" }}
                  </template>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-between mt-12 align-items-center">
              <div class="fw-light">
                <div v-if="!!clientCase.travel_type" class="d-flex flex-wrap align-items-center">
                  <span v-if="!!clientCase.travel_type" class="dot-divider mx-12" />

                  <span v-if="!!clientCase.travel_type" class="fw-bold fs-5">
                    {{ findConceptByKey("tour.travel_type", clientCase.travel_type).name }}
                  </span>

                  <template v-if="!clientCase.is_draft">
                    <span class="dot-divider mx-12" />

                    <span class="d-flex flex-wrap align-items-center gap-12">
                      <div class="tooltip-wrapper">
                        <div class="category-badge category-Q">Q</div>
                        <div class="tooltip-text">Quoted</div>
                      </div>
                      <div class="tooltip-wrapper">
                        <div class="category-badge category-I">I</div>
                        <div class="tooltip-text">Invoiced</div>
                      </div>
                      <div class="tooltip-wrapper">
                        <div class="category-badge category-T">T</div>
                        <div class="tooltip-text">Accountability</div>
                      </div>
                      <div class="tooltip-wrapper">
                        <div class="category-badge category-A">A</div>
                        <div class="tooltip-text">Travel Document Sent</div>
                      </div>
                      <div class="tooltip-wrapper">
                        <div class="category-badge category-L">L</div>
                        <div class="tooltip-text">Luggage Label Sent</div>
                      </div>
                    </span>
                  </template>
                </div>

                <div class="d-flex flex-wrap align-items-center gap-12 mt-8" v-if="!clientCase.is_draft">
                  <span class="dot-divider mx-12" />
                  <v-button
                    variant="icon btn-sm btn-primary"
                    class="category-badge p-8"
                    @click="
                      duplicateCase = true;
                      caseId = Number(clientCase.id);
                    "
                  >
                    <span class="mi mi-copy text-sm" />
                  </v-button>
                  <v-button
                    variant="icon btn-sm btn-primary"
                    class="category-badge p-8"
                    @click="
                      caseConnectLuggage = true;
                      caseId = Number(clientCase.id);
                    "
                  >
                    <v-icon icon="luggage-cart" size="xs" />
                  </v-button>
                  <v-button
                    variant="icon btn-sm btn-primary"
                    class="category-badge p-8"
                    @click="
                      customNotes = true;
                      caseId = Number(clientCase.id);
                    "
                  >
                    <v-icon icon="export" size="xs" />
                  </v-button>
                </div>
              </div>

              <div class="d-flex align-items-center gap-8">
                <v-button variant="icon" class="p-0" @click="toggleAccordion(idx)">
                  <v-icon :icon="openIndex === idx ? 'chevron-down' : 'chevron-right'" size="xs" />
                </v-button>

                <router-link
                  v-if="!clientCase.deletedAt"
                  :aria-label="$t('tourClient.tour')"
                  :to="{ name: 'case-v2.setup.show', params: { caseId: clientCase.id } }"
                  class="btn btn-icon btn-view rounded-pill bg-secondary h-fit-content"
                >
                  <v-icon icon="arrow-right" size="xs" />
                </router-link>

                <v-button
                  v-else
                  class="btn-view rounded-pill bg-secondary h-fit-content"
                  size="xs"
                  variant="icon"
                  @click="setCurrentclientCase(clientCase)"
                >
                  <v-icon icon="arrow-right" />
                </v-button>
              </div>
            </div>

            <div v-if="openIndex === idx" class="mt-12 pt-12 border-top">
              <div class="table-responsive">
                <table class="table table-bordered table-sm mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Category</th>
                      <th>Payment</th>
                      <th>Invoice</th>
                      <th>Booking</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="clientCase.extras && Object.keys(clientCase.extras).length > 0">
                      <tr v-for="(values, category) in clientCase.extras" :key="category">
                        <td class="text-capitalize">{{ category }}</td>
                        <td>{{ values.payment }}</td>
                        <td>{{ values.invoice }}</td>
                        <td>{{ values.booking }}</td>
                      </tr>
                    </template>
                    <tr v-else>
                      <td colspan="4" class="text-center">No extra details available</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>

        <div v-else>
          {{ $t("clientCase.noCases") }}
        </div>
      </div>

      <v-pagination
        v-if="!!clientCases && clientCases.length > 0"
        :meta="pagination.meta"
        :pagination-links="pagination.links"
        class="mt-32 ms-12 me-12"
        @page-changed="
          changePage($event);
          handlePageChangedEvent($event);
        "
        @per-page-changed="
          perPage = $event;
          doSearch();
        "
      />
    </div>

    <the-case-filters-offcanvas
      v-if="offCanvasToShow === 'the-case-filters-offcanvas'"
      v-model:assignedTo="assignedTo"
      v-model:clientId="clientId"
      v-model:destination="destination"
      v-model:endDate="endDate"
      v-model:initDate="initDate"
      v-model:invoicesStatus="invoicesStatus"
      v-model:location="location"
      v-model:onlyOnWay="onlyOnWay"
      v-model:origin="origin"
      v-model:paxNumber="paxNumber"
      v-model:paymentsStatus="paymentsStatus"
      v-model:reservationStatus="reservationStatus"
      v-model:startingSoon="startingSoon"
      v-model:status="status"
      v-model:travelType="travelType"
      :agency="agency.slug"
      @closed="closeOffCanvas"
      @selected="doSearch"
    />

    <the-case-archived-offcanvas
      v-if="offCanvasToShow === 'the-case-archived-offcanvas'"
      :client-tour="currentclientCase"
      @closed="
        closeOffCanvas();
        cleanCurrentclientCase();
      "
      @reload-cases="loadClientCases"
    />
  </div>
  <v-modal
    v-if="showDetailsModal"
    @closed="
      closeClientModal();
      generalShow = false;
    "
  >
    <modal-header class="header-container">
      <h5>{{ caseModalData["caseName"] }}</h5>
    </modal-header>
    <modal-body class="modalMessage">
      <table class="modalTable">
        <tbody>
          <tr>
            <th>{{ $t("general.shared.clientName") }}</th>
            <td>{{ caseModalData["clientName"] }}</td>
          </tr>
          <tr>
            <th>{{ $t("general.shared.clientEmail") }}</th>
            <td>{{ caseModalData["clientEmail"] }}</td>
          </tr>
          <tr>
            <th>{{ $t("general.shared.pax") }}</th>
            <td>{{ caseModalData["paxNumber"] }}</td>
          </tr>
          <tr>
            <th>{{ $t("general.shared.initDate") }}</th>
            <td>{{ $str.formatDateTime(caseModalData["initDate"], settings.formatDate, false) }}</td>
          </tr>
          <tr>
            <th>{{ $t("general.shared.endDate") }}</th>
            <td>{{ $str.formatDateTime(caseModalData["endDate"], settings.formatDate, false) }}</td>
          </tr>
          <tr>
            <th>{{ $t("general.shared.origin") }}</th>
            <td>{{ caseModalData["origin"] }}</td>
          </tr>
          <tr>
            <th>{{ $t("general.shared.destination") }}</th>
            <td>{{ caseModalData["destination"] }}</td>
          </tr>
          <tr>
            <th>{{ $t("general.shared.travelType") }}</th>
            <td>{{ capitalizeFirstLetter(caseModalData["travelType"]) }}</td>
          </tr>
          <tr v-if="otherRoomRequirements">
            <th>{{ $t("clientCase.otherRoomRequirements") }}</th>
            <td>
              <div id="case-tour-other-room-text" class="html-editor-display" v-html="otherRoomRequirements"></div>
            </td>
          </tr>
        </tbody>
      </table>
      <table class="modalTable" v-if="rooming.length !== 0 && rooming[0]?.pax !== null">
        <tr>
          <th class="modalMainHeadings">Rooming Details</th>
        </tr>
        <tr>
          <th class="headingBoarder">Room Type</th>
          <th class="headingBoarder">{{ $t("general.shared.pax") }}</th>
        </tr>
        <tbody>
          <tr v-for="(room, index) in rooming" :key="index">
            <td>{{ room?.room_type }}</td>
            <td class="tripDetailsHeader">{{ room?.pax }}</td>
          </tr>
        </tbody>
      </table>
      <p v-if="!generalShow">Please contact admin for any further assistance.</p>
    </modal-body>
  </v-modal>

  <TheCaseConnectLuggage
    v-if="caseConnectLuggage"
    :agency="agency.slug"
    :case-id="caseId"
    @loadClientCases="loadClientCases"
    @closed="
      caseConnectLuggage = false;
      caseId = 0;
    "
  />

  <TheCaseCustomNotes
    v-if="customNotes"
    :agency="agency.slug"
    :case-id="caseId"
    :notes="invoiceNotes"
    @loadClientCases="loadClientCases"
    @closed="
      customNotes = false;
      caseId = 0;
    "
  />

  <TheCaseDuplicateModal
    v-if="duplicateCase"
    :agency="agency.slug"
    :case-id="caseId"
    @loadClientCases="loadClientCases"
    @closed="
      duplicateCase = false;
      caseId = 0;
    "
  />

  <TheCaseHistoryModal
    v-if="showCaseHistoryModal"
    :agency="agency.slug"
    :caseHistoryModalData="caseHistoryModalData"
    @closed="showCaseHistoryModal = false"
  />
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { VInputTextSearch, VForm, VFormInput, VCheckbox } from "@uniqoders/form";
import qs from "qs";
import querystring from "query-string";
import { storeToRefs } from "pinia";
import { DateTime } from "luxon";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import api from "@/api";
import withTimeout from "@/helpers/timeOut";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import VListHeader from "@/components/shared/VListHeader.vue";
import VPagination from "@/components/vendor/basic/table/VPagination.vue";
import usePagination from "@/helpers/pagination";
import VFilters from "@/components/vendor/filters/VFilters.vue";
import clientCase from "@/api/objects/ClientTour";
import { useAuthStore } from "@/stores/auth";
import TheCaseFiltersOffcanvas from "@/components/case/TheCaseFiltersOffcanvas.vue";
import TheCaseArchivedOffcanvas from "@/components/case/TheCaseArchivedOffcanvas.vue";
import useConcepts from "@/helpers/Concepts";
import { findConceptByKey, mapConcepts } from "@/helpers/ConceptHelper";
import { useAppStore } from "@/stores/app";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { onMounted } from "vue";
import { useToast } from "vue-toastification";
import { onUnmounted } from "vue";
import vSelect from "vue-select";
import TheCaseConnectLuggage from "@/components/case/V2/TheCaseConnectLuggage.vue";
import TheCaseCustomNotes from "@/components/case/V2/TheCaseCustomNotes.vue";
import TheCaseDuplicateModal from "@/components/case/V2/TheCaseDuplicateModal.vue";
import TheCaseHistoryModal from "@/components/case/parts/TheCaseHistoryModal.vue";
import VTable from "@/components/vendor/basic/table/VTable.vue";
import VTableHeader from "@/components/vendor/basic/table/VTableHeader.vue";
import VTableBody from "@/components/vendor/basic/table/VTableBody.vue";

const { offCanvasToShow, openOffCanvas, closeOffCanvas } = useOffCanvasUtils();
const { pagination, changePage, setResponseV2 } = usePagination();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const authStore = useAuthStore();
const { agency } = storeToRefs(authStore);
const appStore = useAppStore();
const { settings } = storeToRefs(appStore);
const toast = useToast();
const concepts = useConcepts();

const rooming = ref<any[]>([]);
const otherRoomRequirements = ref(null);
const showDetailsModal = ref(false);
const clientCases = ref<clientCase[]>([]);
const status = ref<string>("active");
const clientName = ref<string>("");
const initDate = ref<string>("");
const endDate = ref<string>("");
const travelType = ref<string>("");
const paxNumber = ref<string>("");
const reservationStatus = ref<string>("");
const paymentsStatus = ref<string>("");
const invoicesStatus = ref<string>("");
const destination = ref<string>("");
const location = ref<string>("");
const origin = ref<string>("");
const clientId = ref<string>("");
const assignedTo = ref<string>("");
const onlyOnWay = ref<boolean>(false);
const startingSoon = ref<any>("");
const caseModalData = ref<any>({});
const perPage = ref<number>(15);
const page = ref<number>(1);
const loaded = ref(false);
const currentclientCase = ref<clientCase | null>(null);
const filterOptions = ref<Record<string, any>[]>([]);
const generalShow = ref<boolean>(false);
const openIndex = ref<number | null>(null);
const caseStartingInDays = ref<string>("");
const hotelPaidUntil = ref<string>("");
const duplicateCase = ref<boolean>(false);
const customNotes = ref<boolean>(false);
const caseConnectLuggage = ref<boolean>(false);
const showCaseHistoryModal = ref<boolean>(false);
const caseHistoryModalData = ref<any>({});
const caseId = ref<number>(0);
const invoiceNotes = ref<string>("");

// ⬇️ Order
const order = ref<{ key: string; order: string }>({ key: "", order: "" });

const timers = ref<{ name: any }>({
  name: null,
});

const filters = ref([
  {
    variable: "status",
    key: "status",
    labelAux: "general.shared.filterStatus",
    conceptName: "app.states",
    multiple: false,
    isConcept: true,
  },
  {
    variable: "clientName",
    key: "client_name",
    labelAux: "general.shared.filterSearch",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "initDate",
    key: "init_date",
    labelAux: "general.shared.filterInitDate",
    multiple: false,
    isConcept: false,
  },
  { variable: "endDate", key: "end_date", labelAux: "general.shared.filterEndDate", multiple: false, isConcept: false },
  {
    variable: "travelType",
    key: "travel_type",
    labelAux: "general.shared.filterTravelType",
    conceptName: "tour.travel_type",
    multiple: false,
    isConcept: true,
  },
  {
    variable: "paxNumber",
    key: "pax_number",
    labelAux: "general.shared.filterPaxNumber",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "reservationStatus",
    key: "reservation_status",
    labelAux: "general.shared.filterReservationStatus",
    conceptName: "tour.reservation_status_type",
    multiple: false,
    isConcept: true,
  },
  {
    variable: "paymentsStatus",
    key: "payments_status",
    labelAux: "general.shared.filterPaymentsStatus",
    conceptName: "tour.payment_status_type",
    multiple: false,
    isConcept: true,
  },
  {
    variable: "invoicesStatus",
    key: "invoices_status",
    labelAux: "general.shared.filterInvoicesStatus",
    conceptName: "tour.invoice_status_type",
    multiple: false,
    isConcept: true,
  },
  {
    variable: "clientId",
    key: "client_id",
    labelAux: "general.shared.filterClient",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "destination",
    key: "destination",
    labelAux: "general.shared.filterDestination",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "location",
    key: "location",
    labelAux: "general.shared.filterLocation",
    multiple: false,
    isConcept: false,
  },
  { variable: "origin", key: "origin", labelAux: "general.shared.filterOrigin", multiple: false, isConcept: false },
  {
    variable: "assignedTo",
    key: "assigned_to",
    labelAux: "general.shared.filterManager",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "startingSoon",
    key: "starting_soon",
    labelAux: "general.shared.filterStartingSoon",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "onlyOnWay",
    key: "only_case",
    labelAux: "general.shared.filterOnlyOnWay",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "caseStartingInDays",
    key: "case_starting_in_days",
    labelAux: "general.shared.filterCaseStartingInDays",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "hotelPaidUntil",
    key: "hotel_paid_until",
    labelAux: "general.shared.filterHotelPaidUntil",
    multiple: false,
    isConcept: false,
  },
]);

const filterRefs = {
  status,
  clientName,
  initDate,
  endDate,
  travelType,
  paxNumber,
  reservationStatus,
  paymentsStatus,
  invoicesStatus,
  destination,
  location,
  origin,
  clientId,
  assignedTo,
  onlyOnWay,
  startingSoon,
  caseStartingInDays,
  hotelPaidUntil,
};

onMounted(async () => {
  await setQueryParametersAsFilters();
  await doSearch();
});

const buildOptions = (key: string) => {
  if (concepts.concepts.value && concepts.concepts.value[key]) {
    const list = mapConcepts(concepts.concepts.value[key]);

    list.unshift({
      key: "",
      label: t("general.shared.all"),
      permission: null,
      value: null,
      order: 0,
      status: "active",
      icon: "",
      color: "",
      shortName: "all",
    });

    return list;
  }
  return [];
};

// ✅ Computed values
const reservationStatusOptions = computed(() => buildOptions("tour.reservation_status_type"));

const paymentsStatusOptions = computed(() => buildOptions("tour.payment_status_type"));

const invoicesStatusOptions = computed(() => buildOptions("tour.invoice_status_type"));

const capitalizeFirstLetter = (str: string): string => {
  return str?.charAt(0)?.toUpperCase() + str?.slice(1);
};

const openCaseDetailsModal = (clientCase: any) => {
  showDetailsModal.value = true;

  caseModalData.value = {
    caseName: clientCase?.name || "--",
    clientName: clientCase?.client?.name || "--",
    clientEmail: clientCase?.client?.email || "--",
    initDate: clientCase?.start_date || "--",
    endDate: clientCase?.end_date || "--",
    paxNumber: clientCase?.pax_count || "--",
    origin: clientCase?.origin?.name || "--",
    destination: clientCase?.destination?.name || "--",
    travelType: clientCase?.travel_type || "--",
  };

  rooming.value = clientCase?.rooming || [];
  otherRoomRequirements.value = clientCase?.otherRoomRequirements || null;
};

const closeClientModal = () => {
  showDetailsModal.value = false;
};

const openCaseHistoryModal = (clientCase: any) => {
  showCaseHistoryModal.value = true;
  caseHistoryModalData.value = clientCase;
};

const search = () => {
  clearTimeout(timers.value.name);

  timers.value.name = withTimeout(
    async () => {
      await doSearch();
    },
    timers.value.name,
    1000
  );
};

/**
 * Detects any query parameter in the url and loads it in the filters (if available)
 *
 * @returns {Promise<void>}
 */
const setQueryParametersAsFilters = async () => {
  const query = route.query;
  const {
    filters: filterQuery,
    order: orderQuery,
    perPage: perPageQuery,
    page: pageQuery,
    status: statusQuery,
  }: any = qs.parse(qs.stringify(query));

  if (filterQuery) {
    filters.value.forEach((filter) => {
      if (filterQuery[filter.key] !== undefined && filter.variable in filterRefs) {
        filterRefs[filter.variable].value = filterQuery[filter.key];
      }
    });
  }

  if (orderQuery) {
    order.value = orderQuery;
  }

  const pageNumber = parseInt(pageQuery, 10);
  if (!Number.isNaN(pageNumber)) page.value = pageNumber;

  const perPageNumber = parseInt(perPageQuery, 10);
  if (!Number.isNaN(perPageNumber)) perPage.value = perPageNumber;

  if (statusQuery) {
    status.value = statusQuery;
  }
};

/**
 * Sets the local filters as url query parameters.
 */
const setFiltersAsQueryParameters = async () => {
  const filtersPayload: Record<string, any> = {};

  filters.value.forEach((filter) => {
    const value = filterRefs[filter.variable]?.value;
    if (value !== undefined && value !== null && value !== "" && value !== false) {
      filtersPayload[filter.key] = value;
    }
  });

  const queryObject: any = {
    filters: filtersPayload,
    perPage: perPage.value,
    status: status.value,
    page: page.value,
  };

  if (order.value?.key) {
    queryObject.order = order.value;
  }

  await setFilters();

  let query: any = qs.stringify(queryObject, { encode: false });
  query = querystring.parse(query);

  router.push({
    name: "case-v2.index",
    query,
  });
};

const doSearch = async () => {
  await setFiltersAsQueryParameters();
  await loadClientCases();
};

const loadClientCases = async (customPage?: number) => {
  try {
    loaded.value = false;

    const filtersPayload: Record<string, any> = {};

    filters.value.forEach((filter: any) => {
      const value = filterRefs[filter.variable]?.value;

      if (value !== undefined && value !== null && value !== "" && value !== false) {
        if (filter.key === "init_date" || filter.key === "end_date") {
          filtersPayload[filter.key] = DateTime.fromFormat(value, "dd-MM-yyyy").toFormat("MM-dd-yyyy");
        } else {
          filtersPayload[filter.key] = value;
        }
      }
    });

    const query: any = {
      filters: filtersPayload,
      order: order.value,
      perPage: perPage.value,
      status: status.value,
      page: customPage || page.value,
    };

    if (agency.value) {
      const response = await api.case.allCasesPaginated(agency.value.slug, query);
      setResponseV2(response); // make sure `setResponse` is defined or imported
      clientCases.value = response.data.data;
    }
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || "Failed to load client tours");
  } finally {
    loaded.value = true;
  }
};

const cleanCurrentclientCase = () => {
  currentclientCase.value = null;
};

const handlePageChangedEvent = async (page: number) => {
  await loadClientCases(page);
};

const toggleOrder = (field: string) => {
  if (!order.value.order) {
    order.value.order = "ASC";
  } else {
    order.value.order = order.value.order === "ASC" ? "DESC" : "ASC";
  }

  order.value.key = field;
  search();
};

const setFilters = () => {
  filterOptions.value = filters.value.flatMap((filter) => {
    const { key, multiple, isConcept, labelAux, conceptName, variable } = filter;
    const selected = filterRefs[variable]?.value;

    const options = multiple ? selected || [] : [selected];

    return options
      .filter((opt: any) => opt !== undefined && opt !== null && opt !== "" && opt !== false)
      .map((option: any) => {
        const value = isConcept ? findConceptByKey(`${conceptName}`, option) : option;
        const label = t(labelAux, { value: conceptName ? value.name : value });

        return {
          key,
          value: conceptName ? value.key : value,
          label,
        };
      });
  });
};

const removeFilter = (filter: Record<string, string>) => {
  filters.value.forEach((filterAux: Record<string, string | boolean>) => {
    const variable = filterAux.variable as keyof typeof filterRefs;

    if (filter.key === "only_case") {
      onlyOnWay.value = false;
    }

    if (filterAux.key === filter.key) {
      const refVar = filterRefs[variable];

      if (!filterAux.multiple) {
        refVar.value = "";
      } else {
        refVar.value = (refVar.value || []).filter((option: string) => option !== filter.value);
      }
    }
  });

  if (filter.key === "status") {
    status.value = "active";
  }

  doSearch(); // You must define `doSearch()` in your setup
};

const setCurrentclientCase = (clientCase: any) => {
  currentclientCase.value = clientCase;

  if (offCanvasToShow.value !== "the-case-archived-offcanvas") {
    openOffCanvas("the-case-archived-offcanvas");
  }
};

const toggleAccordion = (idx: number) => {
  openIndex.value = openIndex.value === idx ? null : idx;
};

onUnmounted(() => {
  cleanCurrentclientCase();
});
</script>

<style scoped>
.cases-v2-wide {
  max-width: 1550px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

@media (min-width: 1400px) {
  .cases-v2-wide {
    padding-left: 1.75rem;
    padding-right: 1.75rem;
  }
}

.client-modal-button {
  cursor: pointer;
}
.header-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 15px;
}

/* Style for the close button */
.close-button {
  background: none;
  border: none;
  cursor: pointer;
}
.headingBoarder {
  border: 1px solid #ddd;
  padding: 15px !important;
}
.modalMessage {
  padding: 15px;
}

.modalTable {
  width: 100%;
  margin-bottom: 12px;
}

.modalTable tr {
  border: 1px solid #ddd;
  padding: 12px;
}

.modalTable td {
  border: 1px solid #ddd;
  padding: 12px;
}

.modalTable th {
  padding: 12px;
}

.bg-collaborator {
  background: #dcbb66;
}

.responsive-header {
  width: 105px;
}

.case-type-badge-v1 {
  display: flex;
  width: 24px;
  height: 24px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
}

.cases-list-table-shell {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.table-for-cases :deep(.table) {
  border: 0 !important;
  margin-bottom: 0 !important;
  font-size: 0.9rem;
}

.table-for-cases :deep(thead th) {
  background: #f8f9fa;
  font-size: 0.75rem;
  letter-spacing: 0.3px;
  padding-top: 0.7rem;
  padding-bottom: 0.7rem;
  border: 0 !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.table-for-cases :deep(tbody td) {
  padding-top: 0.6rem;
  padding-bottom: 0.6rem;
  border: 0 !important;
}

.table-for-cases :deep(tbody tr:not(:last-child) td) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
}

.category-badge {
  display: flex;
  width: 30px;
  height: 30px;
  font-size: 14px;
  font-weight: bold;
  color: white;
  border-radius: 4px;
  justify-content: center;
  align-items: center;
}

.table-for-cases .category-badge {
  width: 30px !important;
  height: 30px !important;
  font-size: 14px !important;
  border-radius: 6px !important;
}

.table-for-cases :deep(.btn-view .icon),
.table-for-cases :deep(.btn-view .mi) {
  font-size: 14px !important;
}

.table-for-cases :deep(.btn-view) {
  width: 30px !important;
  height: 30px !important;
}

.category-Q { background-color: #e61275; color: #fff; } /* Quoted */
.category-I { background-color: #26d0a1; color: #111; } /* Invoiced */
.category-A { background-color: #2c2b2b; color: #fff; } /* Accountability */
.category-C { background-color: #e65c51; color: #111; } /* Cancelled */
.category-T { background-color: #ff9b64; color: #111; } /* Travel Document */
.category-L { background-color: #e6b328; color: #111; } /* Luggage label/passport */

.tooltip-wrapper {
  position: relative;
  display: inline-block;
}

.tooltip-text {
  visibility: hidden;
  background-color: #333;
  color: #fff;
  text-align: center;
  padding: 4px 8px;
  border-radius: 4px;
  position: absolute;
  z-index: 1000;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 11px;
  opacity: 0;
  transition: opacity 0.2s;
}

.tooltip-text::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #333 transparent transparent transparent;
}

.tooltip-wrapper:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

.draft {
  background-color: #ee2a7b !important;
}

.web-booking {
  background-color: #dc3545 !important;
}

.cutom-notes {
  background-color: #32de84 !important;
  border-color: #32de84 !important;
}

.filter-section-compact {
  background: #f8f9fa;
  padding: 9px 4px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  font-weight: 600;
  margin: 0 0 0 0;
}

.v-select-compact :deep(.vs__dropdown-toggle) {
  min-height: 32px;
  padding: 0;
}

.v-select-compact :deep(.vs__selected) {
  margin: 2px;
  font-size: 0.875rem;
  font-weight: 600;
}

.v-select-compact :deep(.vs__actions) {
  padding: 2px 6px;
}

.form-control-compact :deep(.v-form-input-field) {
  height: 32px;
  padding: 4px 8px;
  font-size: 0.875rem;
  font-weight: 600;
}

:deep(.v-form-label) {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 2px;
  letter-spacing: 0.4px;
  display: block;
}

.filter-section-compact :deep(.form-check-label) {
  font-weight: 700;
}
</style>
